/**
 * Cross-Policy Conflicts View
 *
 * Surfaces detected contradictions between policies. Compliance admins
 * can acknowledge, resolve, or dismiss each conflict. Each row shows the
 * exact quoted text from both policies so findings are auditable.
 */
import React, { useState, useEffect, useCallback } from 'react';
import InfoTooltip from '../../components/InfoTooltip';
import NotesModal from './NotesModal';
import {
  HashtagIcon,
  UserIcon,
  BookOpenIcon,
  Cog6ToothIcon,
  MagnifyingGlassIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';

const API = process.env.REACT_APP_API_URL;

const SEVERITY_CFG = {
  critical: { label: 'Critical', color: 'bg-red-100 text-red-800 border-red-300' },
  high:     { label: 'High',     color: 'bg-red-50 text-red-700 border-red-200' },
  medium:   { label: 'Medium',   color: 'bg-amber-50 text-amber-700 border-amber-200' },
  low:      { label: 'Low',      color: 'bg-slate-50 text-slate-600 border-slate-200' },
};

const TYPE_CFG = {
  numerical:    { label: 'Numerical',    Icon: HashtagIcon },
  role:         { label: 'Role',         Icon: UserIcon },
  definitional: { label: 'Definitional', Icon: BookOpenIcon },
  procedural:   { label: 'Procedural',   Icon: Cog6ToothIcon },
  scope:        { label: 'Scope',        Icon: MagnifyingGlassIcon },
};

const STATUS_CFG = {
  open:         { label: 'Open',         color: 'bg-red-50 text-red-700 border-red-200' },
  acknowledged: { label: 'Acknowledged', color: 'bg-amber-50 text-amber-700 border-amber-200' },
  resolved:     { label: 'Resolved',     color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  dismissed:    { label: 'Dismissed',    color: 'bg-slate-100 text-slate-500 border-slate-200' },
};

export default function ConflictsView({ onOpenPolicy }) {
  const [conflicts, setConflicts] = useState([]);
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('open');
  const [severityFilter, setSeverityFilter] = useState('');
  const [expanded, setExpanded] = useState({});
  const [scanning, setScanning] = useState(false);
  const [scanMsg, setScanMsg] = useState('');
  const [notesModal, setNotesModal] = useState(null); // { id, status, title, description, confirmLabel, tone }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      if (statusFilter) qs.set('status', statusFilter);
      if (severityFilter) qs.set('severity', severityFilter);
      const [cRes, rRes] = await Promise.all([
        fetch(`${API}/api/ppm/conflicts?${qs}`, { credentials: 'include' }),
        fetch(`${API}/api/ppm/conflicts/runs`, { credentials: 'include' }),
      ]);
      const cData = await cRes.json();
      const rData = await rRes.json();
      if (cData.success) setConflicts(cData.conflicts || []);
      if (rData.success) setRuns(rData.runs || []);
    } catch {}
    setLoading(false);
  }, [statusFilter, severityFilter]);

  useEffect(() => { load(); }, [load]);

  const triggerScan = async () => {
    setScanning(true); setScanMsg('');
    try {
      const res = await fetch(`${API}/api/ppm/conflicts/scan`, {
        method: 'POST', credentials: 'include',
      });
      const data = await res.json();
      setScanMsg(data.success ? 'Scan started — refresh in a minute' : (data.message || 'Failed'));
      setTimeout(load, 30000);
    } catch { setScanMsg('Scan failed'); }
    setScanning(false);
  };

  const updateStatus = async (id, status, notes) => {
    try {
      await fetch(`${API}/api/ppm/conflicts/${id}`, {
        method: 'PATCH', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status, resolution_notes: notes || null }),
      });
      load();
    } catch {}
  };

  const latestRun = runs[0];
  const counts = conflicts.reduce((acc, c) => {
    acc[c.severity] = (acc[c.severity] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="p-8">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-bold text-slate-800 tracking-tight">Cross-Policy Conflicts</h2>
              <InfoTooltip
                title="Cross-Policy Conflicts"
                width={380}
                content={
                  <>
                    Flags cases where <span className="text-white font-medium">two of your own policies contradict each other</span> —
                    e.g., one says credentialing completes in 60 days and another says 90.
                    An auditor who catches it before you has a finding. Review and retire
                    the stale one to keep your library consistent.
                  </>
                }
              />
            </div>
            <p className="text-slate-400 text-xs mt-0.5">
              {conflicts.length} {conflicts.length === 1 ? 'conflict' : 'conflicts'}
              {counts.critical ? ` · ${counts.critical} critical` : ''}
              {counts.high ? ` · ${counts.high} high` : ''}
              {counts.medium ? ` · ${counts.medium} medium` : ''}
            </p>
          </div>
          <button onClick={triggerScan} disabled={scanning}
            className="px-5 py-2.5 rounded-xl bg-slate-800 text-white text-sm font-semibold hover:bg-slate-900 disabled:opacity-50 transition-colors">
            {scanning ? 'Starting...' : 'Run Scan Now'}
          </button>
        </div>

        {scanMsg && (
          <div className="mb-4 p-3 rounded-xl bg-orange-50 border border-orange-200 text-orange-700 text-xs">{scanMsg}</div>
        )}

        {latestRun && latestRun.finished_at && (
          <div className="mb-4 text-xs text-slate-400">
            Last scan: {new Date(latestRun.started_at).toLocaleString()} · {latestRun.policies_scanned} policies · {latestRun.pairs_compared} pairs compared · {latestRun.conflicts_found} conflicts found
          </div>
        )}

        {/* Filters */}
        <div className="flex gap-2 mb-4 flex-wrap items-center">
          <div className="flex gap-1 p-1 bg-slate-100/80 rounded-xl">
            {['open', 'acknowledged', 'resolved', 'dismissed', 'all'].map(s => (
              <button key={s} onClick={() => setStatusFilter(s === 'all' ? '' : s)}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all capitalize ${
                  (statusFilter || 'all') === s ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'
                }`}>{s}</button>
            ))}
          </div>
          <select value={severityFilter} onChange={e => setSeverityFilter(e.target.value)}
            className="rounded-xl border border-slate-200 px-3 py-2 text-xs text-slate-600 focus:ring-2 focus:ring-orange-500 focus:border-orange-500">
            <option value="">All severities</option>
            <option value="critical">Critical</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>
        </div>

        {loading && (
          <div className="space-y-3">
            {[1, 2].map(i => (
              <div key={i} className="bg-white rounded-2xl border border-slate-200 p-6 animate-pulse">
                <div className="h-4 bg-slate-100 rounded w-1/2 mb-2" />
                <div className="h-3 bg-slate-100 rounded w-1/3" />
              </div>
            ))}
          </div>
        )}

        {!loading && conflicts.length === 0 && (
          <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center">
            <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-200 rounded-2xl flex items-center justify-center text-emerald-600">
              <CheckCircleIcon className="w-8 h-8" strokeWidth={1.75} />
            </div>
            <h3 className="text-base font-semibold text-slate-700 mb-1">No conflicts</h3>
            <p className="text-sm text-slate-400">{statusFilter === 'open' ? 'No open conflicts detected. Run a scan to check.' : 'No conflicts match this filter.'}</p>
          </div>
        )}

        {!loading && conflicts.length > 0 && (
          <div className="space-y-3">
            {conflicts.map(c => {
              const sev = SEVERITY_CFG[c.severity] || SEVERITY_CFG.medium;
              const typ = TYPE_CFG[c.conflict_type] || { label: c.conflict_type, Icon: ExclamationTriangleIcon };
              const stat = STATUS_CFG[c.status] || STATUS_CFG.open;
              const isOpen = expanded[c.id];

              return (
                <div key={c.id} className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
                  <button onClick={() => setExpanded(e => ({ ...e, [c.id]: !e[c.id] }))}
                    className="w-full text-left p-5 hover:bg-slate-50/50 transition-colors">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                          <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold border ${sev.color}`}>
                            {sev.label}
                          </span>
                          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-slate-50 text-slate-600 border border-slate-200">
                            {typ.Icon && <typ.Icon className="w-3.5 h-3.5" strokeWidth={1.75} />} {typ.label}
                          </span>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-semibold border ${stat.color}`}>{stat.label}</span>
                          {c.topic && <span className="text-xs text-slate-500">· {c.topic}</span>}
                        </div>
                        <div className="flex items-center gap-2 text-sm text-slate-700 font-medium mb-1">
                          <button onClick={(e) => { e.stopPropagation(); onOpenPolicy(c.policy_a_id); }}
                            className="text-orange-700 hover:underline font-mono text-xs">
                            {c.a_policy_number}
                          </button>
                          <span className="text-slate-400">↔</span>
                          <button onClick={(e) => { e.stopPropagation(); onOpenPolicy(c.policy_b_id); }}
                            className="text-orange-700 hover:underline font-mono text-xs">
                            {c.b_policy_number}
                          </button>
                        </div>
                        <p className="text-sm text-slate-600 leading-relaxed">{c.summary}</p>
                      </div>
                      <svg className={`w-5 h-5 text-slate-400 flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </button>

                  {isOpen && (
                    <div className="border-t border-slate-100 p-5 space-y-4">
                      {/* Side-by-side quotes */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div className="rounded-xl border border-slate-200 bg-red-50/30 p-4">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">{c.a_policy_number} says</span>
                          </div>
                          <p className="text-xs text-slate-700 font-mono whitespace-pre-wrap leading-relaxed">{c.policy_a_quote}</p>
                        </div>
                        <div className="rounded-xl border border-slate-200 bg-red-50/30 p-4">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">{c.b_policy_number} says</span>
                          </div>
                          <p className="text-xs text-slate-700 font-mono whitespace-pre-wrap leading-relaxed">{c.policy_b_quote}</p>
                        </div>
                      </div>

                      {c.recommended_resolution && (
                        <div className="rounded-xl border border-orange-200 bg-orange-50/50 p-4">
                          <div className="text-[10px] font-semibold text-orange-700 uppercase tracking-wide mb-1">Recommended resolution</div>
                          <p className="text-xs text-slate-700 leading-relaxed">{c.recommended_resolution}</p>
                        </div>
                      )}

                      {c.recommendation_id && c.status === 'open' && (
                        <div className="text-xs text-slate-500">
                          A policy recommendation has been created and assigned to the policy owner.
                        </div>
                      )}

                      {c.resolution_notes && (
                        <div className="text-xs text-slate-500 italic">Notes: {c.resolution_notes}</div>
                      )}

                      {c.status === 'open' && (
                        <div className="flex gap-2 pt-2">
                          <button onClick={() => updateStatus(c.id, 'acknowledged')}
                            className="px-4 py-2 rounded-xl bg-amber-600 text-white text-xs font-semibold hover:bg-amber-700 transition-colors">
                            Acknowledge
                          </button>
                          <button onClick={() => setNotesModal({
                              id: c.id, status: 'resolved', tone: 'emerald',
                              title: 'Mark conflict resolved',
                              description: `${c.a_policy_number} ↔ ${c.b_policy_number}: ${c.topic || c.summary}`,
                              placeholder: 'What changed to resolve this conflict? (e.g. which policy was updated and how)',
                              confirmLabel: 'Mark Resolved',
                            })}
                            className="px-4 py-2 rounded-xl bg-emerald-600 text-white text-xs font-semibold hover:bg-emerald-700 transition-colors">
                            Mark Resolved
                          </button>
                          <button onClick={() => setNotesModal({
                              id: c.id, status: 'dismissed', tone: 'orange',
                              title: 'Dismiss conflict',
                              description: `${c.a_policy_number} ↔ ${c.b_policy_number}: ${c.topic || c.summary}`,
                              placeholder: 'Why is this not a real conflict?',
                              confirmLabel: 'Dismiss',
                            })}
                            className="px-4 py-2 rounded-xl border border-slate-200 text-slate-600 text-xs font-medium hover:bg-slate-50 transition-colors">
                            Dismiss (not a conflict)
                          </button>
                        </div>
                      )}

                      {c.status !== 'open' && (
                        <button onClick={() => updateStatus(c.id, 'open', null)}
                          className="text-xs text-slate-500 hover:text-slate-700 font-medium">
                          Reopen
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <NotesModal
          open={!!notesModal}
          title={notesModal?.title}
          description={notesModal?.description}
          placeholder={notesModal?.placeholder}
          confirmLabel={notesModal?.confirmLabel}
          tone={notesModal?.tone}
          onClose={() => setNotesModal(null)}
          onConfirm={(notes) => { updateStatus(notesModal.id, notesModal.status, notes); setNotesModal(null); }}
        />
      </div>
    </div>
  );
}
